import { supabase } from '@/lib/supabase';
import type { InboxItem, InboxQuelle, InboxKlassifizierung, InboxItemStatus, InboxItemUpdate } from './database.types';

export interface InboxFilter {
  eingangs_quelle?: InboxQuelle | '';
  ki_klassifizierung?: InboxKlassifizierung | '';
  status?: InboxItemStatus | '';
  ki_konfidenz_min?: number;
}

export function filterInboxItems(items: InboxItem[], filter: InboxFilter): InboxItem[] {
  return items.filter((item) => {
    if (filter.eingangs_quelle && item.eingangs_quelle !== filter.eingangs_quelle) return false;
    if (filter.ki_klassifizierung && item.ki_klassifizierung !== filter.ki_klassifizierung) return false;
    if (filter.status && item.status !== filter.status) return false;
    if (filter.ki_konfidenz_min !== undefined && filter.ki_konfidenz_min > 0) {
      if ((item.ki_konfidenz ?? 0) < filter.ki_konfidenz_min) return false;
    }
    return true;
  });
}

export function buildStoragePath(fileName: string): string {
  const ts = Date.now();
  const safe = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
  return `uploads/${ts}_${safe}`;
}

export function validateUploadFile(file: File): string | null {
  const MAX_BYTES = 104_857_600; // 100 MB
  if (file.size > MAX_BYTES) return 'Datei zu groß (max. 100 MB)';
  if (file.size === 0) return 'Datei ist leer';
  return null;
}

export function sortInboxItems(items: InboxItem[]): InboxItem[] {
  return [...items].sort(
    (a, b) => new Date(b.eingangs_datum).getTime() - new Date(a.eingangs_datum).getTime(),
  );
}

export const ZIELMODUL_LABELS: Record<string, string> = {
  akte_gutachten:   'Gutachten-Akte',
  akte_autoglas:    'Autoglas-Akte',
  akte_mietwagen:   'Mietwagen-Akte',
  akte_werkstatt:   'Werkstatt-Akte',
  fahrzeug:         'Fahrzeug',
  kontakt:          'Kontakt',
  buchhaltung:      'Buchhaltung',
  forderungsmanager:'Forderungsmanager',
  branding_assets:  'Branding-Assets',
  mitarbeiter:      'Mitarbeiter',
  unbekannt:        'Manuell klären',
};

export function buildBrandingSlug(firma: string, timestamp: number, ext: string): string {
  const safeFirma = firma
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return `logo-${safeFirma}-${timestamp}.${ext}`;
}

export interface SortierUpdateParams {
  zielmodul: string;
  zielId?: string | null;
  userId: string;
}

export function buildSortierUpdate(params: SortierUpdateParams): InboxItemUpdate & { sortiert_am: string } {
  return {
    status: 'sortiert',
    sortiert_in_modul: params.zielmodul,
    sortiert_in_ziel_id: params.zielId ?? null,
    sortiert_durch_user_id: params.userId,
    sortiert_am: new Date().toISOString(),
  };
}

export async function getSignedInboxUrl(itemId: string, expiresIn = 3600): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  const response = await fetch('/api/inbox', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ action: 'getSignedUrl', id: itemId, expires_in: expiresIn }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error || 'Signed URL konnte nicht erstellt werden');
  }

  const json = await response.json() as { signedUrl: string | null };
  return json.signedUrl ?? '';
}
